import { diagnosticBlock, tradeDiagnostic, tradeProviderInfo, tradeSpan, diagnosticOutcome } from "./trade-diagnostics";
import { createWalletClient, custom, getAddress, numberToHex, type Address, type Hex } from "viem";
import { localE2eProvider } from "./local-e2e-provider";
import { preflightNftTransaction, type PreparedNftTransaction, type NftPreflightOptions } from "./transaction-preflight";
import { readRpc, type RpcReadOptions } from "./read-rpc";
import { elysium } from "../config/elysium";
import { configuredElysiumRpcUrl, configuredElysiumMintProvider } from "./elysium-rpc";
import { createMintReadSession, takeReferralMintReads } from "./mint-read-session";
import { walletErrorCode, walletErrorMessage } from "./wallet-errors";

export type Eip1193Provider = {
  request(args: { method: string; params?: readonly unknown[] | object }): Promise<unknown>;
  on?(event: "accountsChanged" | "chainChanged" | "disconnect", listener: (...args: unknown[]) => void): void;
  removeListener?(event: "accountsChanged" | "chainChanged" | "disconnect", listener: (...args: unknown[]) => void): void;
};

export function injectedProvider(): Eip1193Provider | null {
  // The explicit loopback TEST_ONLY adapter must win over browser extensions:
  // the acceptance harness must never prompt or transact through a user's
  // installed wallet. Outside that guarded mode localE2eProvider() is null.
  return localE2eProvider() ?? (globalThis as { ethereum?: Eip1193Provider }).ethereum ?? null;
}

export async function requestAccount(provider: Eip1193Provider): Promise<Address> {
  const accounts = await provider.request({ method: "eth_requestAccounts" });
  if (!Array.isArray(accounts) || typeof accounts[0] !== "string") throw new Error("Wallet did not return an account");
  return getAddress(accounts[0]);
}

export async function currentChainId(provider: Eip1193Provider, options?: RpcReadOptions): Promise<number> {
  const raw = await readRpc(provider, { method: "eth_chainId" }, options);
  if (typeof raw !== "string" || !/^0x[0-9a-f]+$/i.test(raw)) throw new Error("Wallet returned an invalid chain ID");
  const value = Number(BigInt(raw));
  if (!Number.isSafeInteger(value)) throw new Error("Wallet chain ID is outside the safe range");
  return value;
}

type NetworkSwitchStage = "switch" | "add" | "verify";

function networkSwitchErrorMessage(error: unknown, stage: NetworkSwitchStage, name: string): string {
  const code = walletErrorCode(error);
  if (code === 4001) return stage === "add" ?
    "Adding Elysium was rejected in your wallet. Approve adding Elysium to continue." :
    `Network switch was rejected in your wallet. Approve switching to ${name} to continue.`;
  if (code === -32002) return "A wallet request is already pending. Open your wallet and complete or reject it, then try again.";
  if (code === 4200 || code === -32601) return `Your wallet does not support this network request. Add and select ${name} manually in your wallet.`;
  return walletErrorMessage(error, stage === "add" ? "Could not add Elysium. Check your wallet and try again." :
    stage === "verify" ? `Could not verify the selected network. Open your wallet and check that ${name} is selected.` :
    `Could not switch to ${name}. Check your wallet and try again.`);
}

export async function switchChain(provider: Eip1193Provider, chainId: number,
  assertCurrent: () => void = () => {}, resolveRpc = configuredElysiumRpcUrl): Promise<void> {
  if (!Number.isSafeInteger(chainId) || chainId <= 0) throw new RangeError("invalid chain ID");
  const name = chainId === elysium.id ? elysium.name : `chain ${chainId}`;
  const params = [{ chainId: numberToHex(chainId) }];
  let stage: NetworkSwitchStage = "switch";
  let rpcUrl: string | null = null;
  const guard = () => {
    assertCurrent();
    if (rpcUrl && resolveRpc()?.href !== rpcUrl) throw new Error("Elysium RPC configuration changed. Try again.");
  };
  const request = async (method: string, values: readonly unknown[]) => {
    guard();
    await provider.request({ method, params: values });
    guard();
  };
  try {
    try { await request("wallet_switchEthereumChain", params); }
    catch (error) {
      guard();
      if (walletErrorCode(error) !== 4902) throw error;
      // This action may register Elysium only, never an arbitrary target chain.
      if (chainId !== elysium.id) throw new Error(`Add ${name} manually in your wallet, then try switching again.`);
      rpcUrl = resolveRpc()?.href ?? null;
      if (!rpcUrl) throw new Error("Elysium RPC is not configured. The site cannot request adding Elysium. Contact support.");
      stage = "add";
      await request("wallet_addEthereumChain", [{ chainId: numberToHex(elysium.id), chainName: elysium.name,
        nativeCurrency: elysium.nativeCurrency, rpcUrls: [rpcUrl] }]);
      stage = "switch";
      await request("wallet_switchEthereumChain", params);
    }
    stage = "verify";
    const selected = await currentChainId(provider);
    guard();
    if (selected !== chainId) throw new Error(`Wallet did not select ${name}. Open your wallet and switch to ${name}, then try again.`);
  } catch (error) {
    guard();
    throw new Error(networkSwitchErrorMessage(error, stage, name), { cause: error });
  }
}

export async function signSiweMessage(provider: Eip1193Provider, wallet: Address, message: string): Promise<Hex> {
  const client = createWalletClient({ transport: custom(provider, { retryCount: 0 }) });
  return client.signMessage({ account: wallet, message });
}

export async function sendPreparedTransaction(provider: Eip1193Provider, input: PreparedNftTransaction,
  assertContext: () => void = () => {}, onSendRequest: () => void = () => {}, options: NftPreflightOptions = {}): Promise<Hex> {
  const configuredMint = input.chainId === elysium.id && !input.directRedeemIdentity;
  const rpcUrl = configuredMint ? configuredElysiumRpcUrl()?.href : undefined;
  let readProvider = configuredMint ? configuredElysiumMintProvider(options.signal) : provider;
  if (!readProvider) throw new Error("Elysium mint RPC is unavailable. Check the application configuration before retrying.");
  const assertCurrent = () => {
    options.signal?.throwIfAborted(); assertContext();
    if (configuredMint && configuredElysiumRpcUrl()?.href !== rpcUrl) throw new Error("Elysium RPC configuration changed. Review again.");
  };
  assertCurrent();
  const walletChain = await currentChainId(provider, options);
  assertCurrent();
  if (walletChain !== input.chainId) throw new Error(`WRONG_CHAIN:${input.chainId}`);
  if (configuredMint) {
    const warmed = input.referralMint ? await takeReferralMintReads(input.referralMint.result, input.referralMint.evidence) : null;
    assertCurrent();
    readProvider = (warmed ?? await createMintReadSession(readProvider, assertCurrent, options)).provider;
  }
  const assertValidTime = await preflightNftTransaction(readProvider, input, assertCurrent, options);
  const finalChain = await currentChainId(provider, options);
  assertValidTime();
  if (finalChain !== input.chainId) throw new Error(`WRONG_CHAIN:${input.chainId}`);
  const transaction = {
    from: getAddress(input.account),
    to: getAddress(input.to),
    data: input.data,
    value: `0x${input.valueWei.toString(16)}`,
  };
  // All local validation has finished. Persist wallet-request recovery state
  // at this boundary, with no asynchronous gap before invoking the provider.
  onSendRequest();
  const result = await provider.request({ method: "eth_sendTransaction", params: [transaction] });
  if (typeof result !== "string" || !/^0x[0-9a-fA-F]{64}$/.test(result)) throw new Error("Wallet returned an invalid tx hash");
  return result as Hex;
}

export type WalletTransactionReceipt = {
  transactionHash: Hex;
  status: "0x0" | "0x1";
  blockNumber: string;
  blockHash: Hex;
  logs: Array<{ address: Address; data: Hex; topics: Hex[] }>;
};

export async function waitForTransactionReceipt(provider: Eip1193Provider, transactionHash: Hex, options: {
  timeoutMs?: number;
  pollMs?: number;
  signal?: AbortSignal;
  expectedChainId?: number;
} = {}): Promise<WalletTransactionReceipt> {
  const timeoutMs = options.timeoutMs ?? 120_000;
  const pollMs = options.pollMs ?? 1_500;
  const abort = new AbortController();
  const onAbort = () => abort.abort(options.signal?.reason);
  options.signal?.addEventListener("abort", onAbort, { once: true });
  if (options.signal?.aborted) onAbort();
  // A real timer owns the entire wait, including a provider promise that never
  // settles. Per-read timeouts may report uncertainty sooner, never failure.
  const timer = setTimeout(() => abort.abort(new Error("TRANSACTION_CONFIRMATION_TIMEOUT")), timeoutMs);
  const readOptions = { signal: abort.signal };
  const operationId = tradeProviderInfo(provider).operationId;
  let round = 0;
  try { while (true) {
    const pollingEnd = tradeSpan(operationId, "receipt-poll", { round: ++round });
    try {
      abort.signal.throwIfAborted();
      if (options.expectedChainId !== undefined && await currentChainId(provider, readOptions) !== options.expectedChainId) {
        throw new Error(`WRONG_CHAIN:${options.expectedChainId}`);
      }
      const raw = await readRpc(provider, { method: "eth_getTransactionReceipt", params: [transactionHash] }, readOptions);
      abort.signal.throwIfAborted();
      if (options.expectedChainId !== undefined && await currentChainId(provider, readOptions) !== options.expectedChainId) {
        throw new Error(`WRONG_CHAIN:${options.expectedChainId}`);
      }
      abort.signal.throwIfAborted();
      pollingEnd("ok", { found: raw !== null, ...diagnosticBlock(raw) });
      if (raw !== null) {
        tradeDiagnostic(operationId, "receipt-received", diagnosticBlock(raw));
        if (typeof raw !== "object") throw new Error("Wallet returned an invalid transaction receipt");
        const value = raw as Record<string, unknown>;
        if (typeof value.transactionHash !== "string" || value.transactionHash.toLowerCase() !== transactionHash.toLowerCase()) {
          throw new Error("Wallet returned a receipt for a different transaction");
        }
        if ((value.status !== "0x0" && value.status !== "0x1") || typeof value.blockNumber !== "string" ||
          typeof value.blockHash !== "string" || !Array.isArray(value.logs)) {
          throw new Error("Wallet returned an incomplete transaction receipt");
        }
        return {
          transactionHash,
          status: value.status,
          blockNumber: value.blockNumber,
          blockHash: value.blockHash as Hex,
          logs: value.logs.map((log) => {
            const item = log as Record<string, unknown>;
            if (typeof item.address !== "string" || typeof item.data !== "string" || !Array.isArray(item.topics)) {
              throw new Error("Wallet returned an invalid receipt log");
            }
            return { address: getAddress(item.address), data: item.data as Hex, topics: item.topics as Hex[] };
          }),
        };
      }
    } catch (error) { pollingEnd(diagnosticOutcome(error)); throw error; }
    await new Promise<void>((resolve, reject) => {
      const onAbort = () => { clearTimeout(timer); reject(abort.signal.reason); };
      const timer = setTimeout(() => {
        abort.signal.removeEventListener("abort", onAbort);
        resolve();
      }, pollMs);
      abort.signal.addEventListener("abort", onAbort, { once: true });
      if (abort.signal.aborted) onAbort();
    });
  } } finally {
    clearTimeout(timer);
    options.signal?.removeEventListener("abort", onAbort);
  }
}

export async function latestBlockTimestamp(provider: Eip1193Provider, options?: RpcReadOptions): Promise<bigint> {
  const raw = await readRpc(provider, { method: "eth_getBlockByNumber", params: ["latest", false] }, options);
  if (!raw || typeof raw !== "object" || typeof (raw as Record<string, unknown>).timestamp !== "string") {
    throw new Error("Wallet returned an invalid latest block");
  }
  return BigInt((raw as Record<string, unknown>).timestamp as string);
}
